import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Download, Printer, X, User, Building, Loader2 } from 'lucide-react';
import OfferPDFPreview from './OfferPDFPreview';
import { fetchIntermediaryData, EMPTY_INTERMEDIARY } from '../../utils/intermediaryData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTEREST_TYPE_LABELS = {
  'fixed_2y': 'Fixă 2 ani',
  'fixed_3y': 'Fixă 3 ani',
  'fixed_5y': 'Fixă 5 ani',
  'fixed_10y': 'Fixă 10 ani',
  'fixed_full': 'Fixă toată perioada',
  'variable': 'Variabilă'
};

const PRODUCT_TYPE_LABELS = {
  'energy_class_a': 'Credit Ipotecar - Clasa Energetică A',
  'energy_class_bc': 'Credit Ipotecar - Clasa Energetică B/C',
  'refinance_a': 'Refinanțare - Clasa Energetică A',
  'refinance_bc': 'Refinanțare - Clasa Energetică B/C'
};

const OfferPreview = ({
  open,
  onClose,
  results,
  clientName: initialClientName,
  loanAmount,
  loanPeriodMonths,
  interestType,
  productType,
  irccRate,
  propertyPrice,
  downPayment
}) => {
  const [loading, setLoading] = useState(false);
  const [clientFullName, setClientFullName] = useState(initialClientName || '');
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const printRef = useRef();

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/calculator/offer-pdf`,
        {
          intermediaryData: getIntermediaryData(),
          clientData: { fullName: clientFullName.toUpperCase(), cnp: '' },
          creditParams: getCreditParams(),
          bankOffers: transformResultsForPDF()
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Oferta_${clientFullName.trim().replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Eroare la descărcarea PDF:', error);
      let detail = '';
      try {
        const text = error?.response?.data instanceof Blob ? await error.response.data.text() : '';
        detail = text ? JSON.stringify(JSON.parse(text).detail) : '';
      } catch { /* ignore */ }
      alert(`Eroare la generarea PDF. Vă rugăm încercați din nou.${detail ? `\n\nDetalii: ${detail}` : ''}`);
    } finally {
      setDownloadingPDF(false);
    }
  };
  
  const [intermediaryData, setIntermediaryData] = useState(EMPTY_INTERMEDIARY);
  useEffect(() => {
    if (!open) return;
    fetchIntermediaryData()
      .then(setIntermediaryData)
      .catch((err) => console.error('Nu s-au putut prelua datele firmei:', err));
  }, [open]);
  const getIntermediaryData = () => intermediaryData;

  const formatCurrency = (value) => {
    const num = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '-';
    return new Intl.NumberFormat('ro-RO').format(Math.round(num)) + ' RON';
  };

  const formatPercent = (value) => {
    const num = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(num)) return '-';
    return num.toFixed(2) + '%';
  };

  // Transformă rezultatele calculatorului în formatul pentru PDF
  const transformResultsForPDF = () => {
    return results.map(result => ({
      bankName: result.bank_name,
      productName: PRODUCT_TYPE_LABELS[productType] || 'Credit Ipotecar',
      creditType: PRODUCT_TYPE_LABELS[productType] || 'Credit Ipotecar',
      fixedRate: result.fixed_rate ? `${INTEREST_TYPE_LABELS[interestType]} - ${formatPercent(result.fixed_rate)}` : '-',
      variableRate: result.variable_rate && !Number.isNaN(Number(irccRate)) && irccRate !== '' && irccRate !== null
        ? `IRCC + ${(Number(result.variable_rate) - Number(irccRate)).toFixed(2)}%`
        : '-',
      ircc: formatPercent(irccRate),
      monthlyPaymentFixed: formatCurrency(result.monthly_payment_fixed_no_insurance),
      monthlyPaymentFixedWithInsurance: formatCurrency(result.monthly_payment_fixed_with_insurance),
      monthlyPaymentVariable: formatCurrency(result.monthly_payment_variable_no_insurance),
      monthlyPaymentVariableWithInsurance: formatCurrency(result.monthly_payment_variable_with_insurance),
      dae: formatPercent(result.dae),
      analysisCommission: formatCurrency(result.cost_breakdown?.analysis_fee || 0),
      adminCommission: result.cost_breakdown?.admin_fee_monthly
        ? formatCurrency(result.cost_breakdown.admin_fee_monthly) + '/lună'
        : '0 RON',
      archiveFee: formatCurrency(result.cost_breakdown?.rnpm || 0),
      evaluationFeeApartment: formatCurrency(result.cost_breakdown?.apartment_evaluation || 0),
      evaluationFeeHouse: formatCurrency(result.cost_breakdown?.house_evaluation || 0),
      lifeInsurance: result.insurance_costs?.life_insurance_rate 
        ? formatPercent(result.insurance_costs.life_insurance_rate) 
        : 'opțional',
      padInsurance: result.insurance_costs?.pad_insurance 
        ? formatCurrency(result.insurance_costs.pad_insurance) + '/an'
        : 'obligatorie',
      optionalPropertyInsurance: result.insurance_costs?.property_insurance 
        ? formatCurrency(result.insurance_costs.property_insurance) + '/an'
        : 'opțional',
      earlyRepaymentCommission: result.insurance_costs?.early_repayment_commission 
        ? formatPercent(result.insurance_costs.early_repayment_commission)
        : '0%',
      preApprovalValidity: result.approval_validity_days 
        ? `${result.approval_validity_days} zile`
        : '90 zile',
      totalCost: formatCurrency(result.total_costs || 0)
    }));
  };

  // Calculează parametrii creditului pentru PDF
  const getCreditParams = () => {
    const loanAmountNum = parseFloat(loanAmount) || 0;
    const propertyPriceNum = parseFloat(propertyPrice) || loanAmountNum * (100 / (100 - (downPayment || 25)));
    const downPaymentAmount = propertyPriceNum - loanAmountNum;
    const eurRate = 5; // Presupunem 5 RON/EUR pentru conversie aproximativă
    
    return {
      propertyValueEuro: Math.round(propertyPriceNum / eurRate),
      propertyValueRon: Math.round(propertyPriceNum),
      creditValueRon: Math.round(loanAmountNum),
      downPaymentRon: Math.round(downPaymentAmount),
      periodYears: Math.round(loanPeriodMonths / 12),
      periodMonths: parseInt(loanPeriodMonths),
      maxDebtRatio: "40%/45%",
      creditType: PRODUCT_TYPE_LABELS[productType] || 'Credit Ipotecar'
    };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGeneratePDF = () => {
    if (!clientFullName.trim()) {
      alert('Vă rugăm introduceți numele complet al clientului pentru a genera PDF-ul.');
      return;
    }
    setShowPDFPreview(true);
  };

  // Dacă arătăm preview-ul PDF
  if (showPDFPreview) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-[1200px] max-h-[95vh] overflow-y-auto p-0">
          {/* Control Bar */}
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10 print:hidden">
            <Button variant="outline" onClick={() => setShowPDFPreview(false)}>
              ← Înapoi
            </Button>
            <h2 className="text-lg font-semibold">
              Ofertă PDF - {clientFullName}
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Printează
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleDownloadPDF} disabled={downloadingPDF} data-testid="offer-download-pdf-btn">
                {downloadingPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {downloadingPDF ? 'Se generează...' : 'Descarcă PDF'}
              </Button>
            </div>
          </div>

          {/* PDF Preview */}
          <div className="p-4 print:p-0" ref={printRef}>
            <OfferPDFPreview
              intermediaryData={getIntermediaryData()}
              clientData={{
                fullName: clientFullName.toUpperCase(),
                cnp: ''
              }}
              creditParams={getCreditParams()}
              bankOffers={transformResultsForPDF()}
            />
          </div>

          {/* Print Styles */}
          <style>{`
            @media print {
              @page {
                size: A4 landscape;
                margin: 8mm;
              }
              body {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .print\\:hidden {
                display: none !important;
              }
            }
          `}</style>
        </DialogContent>
      </Dialog>
    );
  }

  // Formularul pentru introducerea numelui clientului
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="w-5 h-5 text-blue-600" />
            Generare Ofertă PDF
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info despre ofertă */}
          <div className="bg-blue-50 rounded-lg p-4 text-sm">
            <p className="font-medium text-blue-900 mb-2">
              Veți genera o ofertă PDF cu {results.length} {results.length === 1 ? 'bancă' : 'bănci'} selectate:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-1">
              {results.map((r, idx) => (
                <li key={idx}>{r.bank_name}</li>
              ))}
            </ul>
          </div>

          {/* Parametri credit */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-gray-600">
              <span>Valoare credit:</span>
              <span className="font-medium text-gray-900">{formatCurrency(loanAmount)}</span>
              <span>Perioadă:</span>
              <span className="font-medium text-gray-900">{Math.round(loanPeriodMonths / 12)} ani ({loanPeriodMonths} luni)</span>
              <span>Tip produs:</span>
              <span className="font-medium text-gray-900">{PRODUCT_TYPE_LABELS[productType]}</span>
              <span>Tip dobândă:</span>
              <span className="font-medium text-gray-900">{INTEREST_TYPE_LABELS[interestType]}</span>
            </div>
          </div>

          {/* Nume client */}
          <div className="space-y-2">
            <Label htmlFor="clientFullName" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Numele complet al clientului *
            </Label>
            <Input
              id="clientFullName"
              value={clientFullName}
              onChange={(e) => setClientFullName(e.target.value)}
              placeholder="Ex: POPESCU ION-ALEXANDRU"
              className="text-lg"
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Numele va apărea pe documentul PDF în formatul introdus.
            </p>
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} className="flex-1">
            <X className="w-4 h-4 mr-2" />
            Anulează
          </Button>
          <Button 
            onClick={handleGeneratePDF}
            disabled={!clientFullName.trim()}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Generează Ofertă PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfferPreview;
